// NOTE: using apiClient.fetch because bookmarks api is not APIResponse compatible
// endpoint needs to be refactored so we can use apiClient.get instead of apiClient.fetch
/* eslint-disable local-rules/deprecated-member-access, @typescript-eslint/no-use-before-define */

import queryString from 'query-string';
import {reporter} from 'common/report/error-reporting';
import {apiHost} from 'common/report/util';
import {apiClient, FetchOptions} from 'common/util/api-client';
import moment from 'moment';
import mapKeys from 'lodash/mapKeys';

export type BookmarkType =
  | `addiction`
  | `dashboard`
  | `formulas`
  | `funnel-query`
  | `funnels`
  | `insights`
  | `jql-console`
  | `launch-analysis`
  | `retention`
  | `revenue`
  | `segmentation`
  | `segmentation3`
  | `users`
  | `flows`;

// TODO: consider making this more RESTful on the API-side
enum BookmarkOperationType {
  Create = `create`,
  Delete = `delete`,
  List = `list`,
  Update = `update`,
  GetById = `get by id`,
}

export interface BookmarkSaveParams {
  description?: string;
  icon?: string;
  is_modification_restricted?: boolean;
  is_visibility_restricted?: boolean;
  name?: string;
  params: string;
}

type ScopeKey = `projectId` | `workspaceId`;
export type ProjectScope = {[key in ScopeKey]?: number};

// generic bookmark type
export interface SavedBookmark<BookmarkParamsType> {
  can_update_basic?: boolean;
  can_update_restricted?: boolean;
  can_update_visibility?: boolean;
  created: string;
  deleted?: string;
  description?: string;
  displayOptions?: any;
  icon: string;
  id: number;
  includeInDashboard?: boolean;
  isDefault: boolean;
  is_modification_restricted?: boolean;
  is_visibility_restricted?: boolean;
  modified: moment.Moment;
  modifiedStr?: string;
  name: string;
  originalType: string;
  params: BookmarkParamsType;
  projectID: number;
  tags?: string[];
  title: string;
  type: BookmarkType;
  user: string;
  userID: number;
}

const BOOKMARK_KEY_MAP = Object.freeze({
  is_default: `isDefault`,
  original_type: `originalType`,
  project_id: `projectID`,
  user_id: `userID`,
  include_in_dashboard: `includeInDashboard`,
});

export function createBookmark<BookmarkParamsType>({
  type,
  scope,
  params,
}: {
  type: BookmarkType;
  scope: ProjectScope;
  params: BookmarkSaveParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const url = `${baseEndpoint(type, scope)}/${BookmarkOperationType.Create}`;
  return post(url, params, type, BookmarkOperationType.Create);
}

export function updateBookmark<BookmarkParamsType>({
  type,
  scope,
  bookmarkId,
  params,
}: {
  type: BookmarkType;
  scope: ProjectScope;
  bookmarkId: number;
  params: BookmarkSaveParams;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const url = `${baseEndpoint(type, scope)}/${BookmarkOperationType.Update}/${bookmarkId}`;
  return post(url, params, type, BookmarkOperationType.Update);
}

export function listBookmarks<BookmarkParamsType>({
  type,
  scope,
}: {
  type: BookmarkType;
  scope: ProjectScope;
}): Promise<Array<SavedBookmark<BookmarkParamsType>>> {
  const url = `${baseEndpoint(type, scope)}/${BookmarkOperationType.List}`;
  return processBookmarks(apiClient.fetch(url), type, BookmarkOperationType.List);
}

export function getBookmark<BookmarkParamsType>({
  type,
  scope,
  bookmarkId,
}: {
  type: BookmarkType;
  scope: ProjectScope;
  bookmarkId: number;
}): Promise<SavedBookmark<BookmarkParamsType>> {
  const url = `${baseEndpoint(type, scope)}/${bookmarkId}`;
  return processBookmark(apiClient.fetch(url), type, BookmarkOperationType.GetById);
}

export function deleteBookmark({
  type,
  scope,
  bookmarkId,
}: {
  type: BookmarkType;
  scope: ProjectScope;
  bookmarkId: number;
}) {
  const url = `${baseEndpoint(type, scope)}/${BookmarkOperationType.Delete}/${bookmarkId}`;
  return apiClient.fetch(url, {method: `DELETE`});
}

function baseEndpoint(type: BookmarkType, {projectId, workspaceId}: ProjectScope): string {
  if (!projectId && !workspaceId) {
    throw new Error(`projectId or workpsaceId required to make bookmark request`);
  }

  const projectScope = workspaceId ? `api/app/workspaces/${workspaceId}` : `report/${projectId}`;
  return `${apiHost()}/${projectScope}/bookmarks/${type}`;
}

function post<BookmarkParamsType>(
  url: string,
  data: any,
  type: BookmarkType,
  action: BookmarkOperationType,
): Promise<SavedBookmark<BookmarkParamsType>> {
  const fetchOptions: FetchOptions = {
    body: queryString.stringify(data),
    method: `POST`,
    headers: {
      'Content-Type': `application/x-www-form-urlencoded`,
    },
  };
  return processBookmark(apiClient.fetch(url, fetchOptions), type, action);
}

function checkErrors(response, type, action) {
  if (response[`error`]) {
    const error = `Error performing "${action}" on ${type} bookmark: ${JSON.stringify(response)}`;
    reporter.error(error);
    throw new Error(error);
  }
  return response;
}

function processBookmarks(promise, type: BookmarkType, action: BookmarkOperationType) {
  return promise
    .then((response) => response.json())
    .then((responseJson) => checkErrors(responseJson, type, action))
    .then((responseJson) => responseJson.bookmarks.map((bookmark) => formatBookmark(bookmark)))
    .catch((err) => {
      throw new Error(err);
    });
}

function processBookmark<BookmarkParamsType>(
  promise,
  type: BookmarkType,
  action: BookmarkOperationType,
): Promise<SavedBookmark<BookmarkParamsType>> {
  return promise
    .then((response) => response.json())
    .then((responseJson) => checkErrors(responseJson, type, action))
    .then((responseJson) => formatBookmark(responseJson.bookmark))
    .catch((err) => {
      throw new Error(err);
    });
}

function formatBookmark<BookmarkParamsType>(bookmark): SavedBookmark<BookmarkParamsType> {
  // Moment is used to correctly parse the modified date across different browsers;
  // before this additional fomatting, bookmark.modified is not understood by some browsers
  const utc = moment.utc(bookmark.modified).local();
  return {
    ...mapKeys(bookmark, (_, key) => BOOKMARK_KEY_MAP[key] || key),
    displayOptions: {chartType: bookmark.icon}, // expected by bookmark drawer
    modified: utc,
    modifiedStr: utc.format(`MMM Do, YYYY`),
    params: JSON.parse(bookmark.params),
    title: bookmark.name,
  } as SavedBookmark<BookmarkParamsType>;
}
